import { useState, useEffect, useMemo } from 'react';
import { REGISTRATION_ENABLED } from '@/lib/utils';
import { parseLocalDate } from '@/utils/dateUtils';
import LeagueInfo from './LeagueInfo';
import WeeklyCalendar from './WeeklyCalendar';
import LeagueMatchCard from './LeagueMatchCard';
import LeagueRankingTable from './LeagueRankingTable';
import { Button } from '@/components/ui/button';
import { usePrizes } from '@/hooks/usePrizes';
import { CategoryPrizes } from '../PrizesDialog';
import { useUserRegistration } from '@/hooks/useUserRegistration';
import { useLeagueAttendance } from '@/hooks/useLeagueAttendance';
import { useLeagueMatches } from '@/hooks/useLeagueMatches';
import { useLeagueSchedule, calculateGameDateStatus, extractDateFromFormatted } from '@/hooks/useLeagueSchedule';
import { generateLeagueWeeks, calculateWeeksRemaining, formatTimeRange, calculateWeekEndDate, interleaveRestWeeks } from '@/utils/leagueCalendarUtils';
interface League {
  id: string;
  name: string;
  date: string;
  endDate?: string;
  club: string;
  modality: 'round-robin' | 'escalera-piramidal' | 'sumatoria-juegos';
  gameType: 'singles' | 'doubles';
  sport: 'padel' | 'tennis' | 'pickleball';
  category: 'masculina' | 'femenina' | 'mixtos';
  subcategory: string;
  partner?: string;
  currentStage: string;
  status: 'upcoming' | 'active' | 'finished';
  start_time?: string;
  end_time?: string;
  game_days?: string[];
  total_weeks?: number;
  matches_per_week?: number;
  organizer?: string;
  group_rules?: {
    setsPerMatch?: number;
    goldenPoint?: boolean;
    superTieBreak?: boolean;
    maxGamesPerSet?: number;
  };
  additional_rules?: {
    timing?: {
      warmupMinutes?: number;
      restMinutes?: number;
      sideChangeSeconds?: number;
    };
    conduct?: {
      toleranceMinutes?: number;
    };
  };
  restWeeks?: number[];
}

interface LeagueSumatoriaJuegosViewProps {
  league: League;
  leagueMatches?: {
    matches: any[];
    standings: any[];
  } | null;
  onContactOrganizer?: () => void;
  onRegisterClick?: () => void;
  isUserRegistered?: boolean;
  registrationLoading?: boolean;
}

const LeagueSumatoriaJuegosView = ({ league, leagueMatches, onContactOrganizer, onRegisterClick, isUserRegistered, registrationLoading }: LeagueSumatoriaJuegosViewProps) => {
  const [selectedCalendarWeek, setSelectedCalendarWeek] = useState<any>(null);
  const [selectedEncuentro, setSelectedEncuentro] = useState<number | null>(null);
  
  // Fetch user registration, prizes, and attendance
  const { registration } = useUserRegistration(league.id);
  const { prizes: dbPrizes } = usePrizes(league.id);
  const { uiAttendance, updateAttendance, isUpdating: isUpdatingAttendance } = useLeagueAttendance(league.id);
  
  // Fetch real matches from database
  const { 
    userMatchForEncounter, 
    userTeam,
    opponentTeam,
    opponentAttendanceStatus, 
    isLoading: isLoadingMatches,
    setSelectedEncounter: setMatchSelectedEncounter
  } = useLeagueMatches(league.id, selectedEncuentro || 1);
  
  // Structure prizes for PrizesDialog - CORRECTED: Extract subcategory from prize.category format
  const allCategoryPrizes: CategoryPrizes[] = dbPrizes.reduce((acc: CategoryPrizes[], prize) => {
    // Extract subcategory from format "2a - parejas" or "3era"
    const subcategoryMatch = prize.category.match(/^(\d+[a-z]*)/i);
    const subcategoryFromPrize = subcategoryMatch ? subcategoryMatch[1] : prize.category;
    
    // Use registration category (e.g., "Masculino") or fallback to league subcategory
    const mainCategory = registration?.category || league.subcategory?.split(' ')[0] || 'General';
    
    const existingCategory = acc.find(p => 
      p.category === mainCategory && p.subcategory === subcategoryFromPrize
    );
    
    const prizeData = {
      type: prize.description.toLowerCase().includes('trofeo') ? 'trofeo' as const :
            prize.description.toLowerCase().includes('raqueta') || prize.description.toLowerCase().includes('pala') ? 'raqueta' as const :
            prize.description.toLowerCase().includes('$') || prize.description.toLowerCase().includes('€') || prize.amount ? 'dinero' as const :
            'certificado' as const,
      amount: prize.amount,
      description: prize.description
    };
    
    const isFirstPlace = prize.description.toLowerCase().includes('1er') || 
                         prize.description.toLowerCase().includes('primer');
    
    if (existingCategory) {
      if (isFirstPlace) {
        existingCategory.firstPlace.push(prizeData);
      } else {
        existingCategory.secondPlace = existingCategory.secondPlace || [];
        existingCategory.secondPlace.push(prizeData);
      }
    } else {
      acc.push({
        category: mainCategory,
        subcategory: subcategoryFromPrize,
        firstPlace: isFirstPlace ? [prizeData] : [],
        secondPlace: isFirstPlace ? [] : [prizeData]
      });
    }
    return acc;
  }, []);
  
  const userCategory = registration?.category && registration?.subcategory 
    ? `${registration.category} ${registration.subcategory}` 
    : undefined;

  // Helper function to parse individual team score from match score
  const getTeamScore = (scoreString: string, isFirstTeam: boolean): string => {
    if (!scoreString) return '0';
    
    // Handle different score formats: "6-3", "6-4, 6-2", etc.
    const sets = scoreString.split(',').map(s => s.trim());
    const teamScores: string[] = [];
    
    sets.forEach(set => {
      const [score1, score2] = set.split('-');
      teamScores.push(isFirstTeam ? score1?.trim() || '0' : score2?.trim() || '0');
    });
    
    return teamScores.join(', ');
  };

  // Mock data for all matches by week
  const allMatchesByWeek: Record<number, any[]> = {
    1: [
      { matchNumber: 1, team1: "TÚ / María González", team2: "Carlos Rivera / Laura Sánchez", score: "6-4", time: "10:00", court: "1" },
      { matchNumber: 2, team1: "TÚ / María González", team2: "Diego López / Carmen Ruiz", score: "7-6", time: "10:20", court: "1" },
      { matchNumber: 3, team1: "TÚ / María González", team2: "Roberto Silva / Elena Torres", score: "3-6", time: "10:40", court: "1" }
    ],
    2: [
      { matchNumber: 1, team1: "TÚ / María González", team2: "Fernando Mora / Isabel Vega", score: "6-3", time: "10:00", court: "2" },
      { matchNumber: 2, team1: "TÚ / María González", team2: "Miguel Castro / Sofía Herrera", score: "6-6", time: "10:20", court: "2" },
      { matchNumber: 3, team1: "TÚ / María González", team2: "Andrés Jiménez / Lucía Mendoza", score: "6-2", time: "10:40", court: "2" }
    ],
    3: [
      { matchNumber: 1, team1: "TÚ / María González", team2: "Juan Pérez / Ana Martín", score: "4-6", time: "10:00", court: "3" },
      { matchNumber: 2, team1: "TÚ / María González", team2: "Carlos Rivera / Laura Sánchez", score: "6-1", time: "10:20", court: "3" },
      { matchNumber: 3, team1: "TÚ / María González", team2: "Diego López / Carmen Ruiz", score: "2-6", time: "10:40", court: "3" }
    ],
    4: [
      { matchNumber: 1, team1: "TÚ / María González", team2: "Roberto Silva / Elena Torres", score: "6-4", time: "10:00", court: "1" },
      { matchNumber: 2, team1: "TÚ / María González", team2: "Fernando Mora / Isabel Vega", score: "5-6", time: "10:20", court: "1" },
      { matchNumber: 3, team1: "TÚ / María González", team2: "Miguel Castro / Sofía Herrera", score: "6-3", time: "10:40", court: "1" }
    ]
  };

  // Future matches
  const futureMatches: Record<number, any[]> = {
    6: [
      { matchNumber: 1, opponent: "Juan Pérez / Ana Martín", time: "10:00", court: "2" },
      { matchNumber: 2, opponent: "Andrés Jiménez / Lucía Mendoza", time: "10:20", court: "2" },
      { matchNumber: 3, opponent: "Carlos Rivera / Laura Sánchez", time: "10:40", court: "2" }
    ],
    7: [
      { matchNumber: 1, opponent: "Diego López / Carmen Ruiz", time: "10:00", court: "3" },
      { matchNumber: 2, opponent: "Roberto Silva / Elena Torres", time: "10:20", court: "3" },
      { matchNumber: 3, opponent: "Fernando Mora / Isabel Vega", time: "10:40", court: "3" }
    ],
    8: [
      { matchNumber: 1, opponent: "Miguel Castro / Sofía Herrera", time: "10:00", court: "1" },
      { matchNumber: 2, opponent: "Juan Pérez / Ana Martín", time: "10:20", court: "1" },
      { matchNumber: 3, opponent: "Andrés Jiménez / Lucía Mendoza", time: "10:40", court: "1" }
    ]
  };

  // Mock data - in real app this would come from API
  // Calculate total matches from database fields
  const totalMatches = (league.total_weeks || 8) * (league.matches_per_week || 3);
  const weeksPlayed = league.status === 'upcoming' ? 0 : league.status === 'finished' ? (league.total_weeks || 8) : 4;
  const matchesPlayed = weeksPlayed * (league.matches_per_week || 3);
  
  const leagueData = {
    leagueName: league.name,
    startDate: league.date,
    endDate: league.endDate || "17 May 2024",
    totalMatches,
    matchesPlayed,
    sport: league.sport,
    modalidad: league.gameType,
    partner: registration?.partner_name || league.partner || '',
    club: league.club,
    category: registration?.category || league.category || '',
    subcategory: registration?.subcategory || league.subcategory || '',
    nextMatchDate: league.status === 'upcoming' ? "Por confirmar" : "Sábado 12 Abr",
    nextMatchTime: league.status === 'upcoming' ? "Por confirmar" : "10:00 AM",
    nextMatchCourt: league.status === 'upcoming' ? "Por confirmar" : "Central",
    opponent: league.status === 'upcoming' ? "Por confirmar" : "Carlos Rivera / Laura Sánchez",
    leagueType: 'sumatoria-juegos' as const
  };

  // Fetch schedule dates from tournament_schedules (organizer-managed)
  const { gameDates, loading: scheduleLoading } = useLeagueSchedule(league.id);
  
  // Generate weeks dynamically - prioritize tournament_schedules data
  const timeRange = formatTimeRange(league.start_time, league.end_time);
  
  const encounterWeeks = useMemo(() => {
    // If we have gameDates from tournament_schedules, use those
    if (gameDates && gameDates.length > 0) {
      const scheduleWeeks = gameDates.map((gd, index) => {
        const status = calculateGameDateStatus(gd.date);
        const isTennis = league.sport.toLowerCase() === 'tennis' || league.sport.toLowerCase() === 'tenis';
        const fullDate = new Date(gd.date);
        return {
          number: index + 1,
          date: isTennis ? extractDateFromFormatted(gd.formattedDate) : gd.formattedDate,
          dateEnd: isTennis ? calculateWeekEndDate(fullDate) : undefined,
          fullDate,
          status,
          time: timeRange
        };
      });
      
      // If no current week, mark the first future as current
      const hasCurrentWeek = scheduleWeeks.some(w => w.status === 'current');
      if (!hasCurrentWeek) {
        const firstFutureIndex = scheduleWeeks.findIndex(w => w.status === 'future');
        if (firstFutureIndex !== -1) {
          scheduleWeeks[firstFutureIndex].status = 'current';
        }
      }
      
      return scheduleWeeks;
    }
    
    // Fallback to calculating from total_weeks
    return generateLeagueWeeks(
      league.date,
      league.total_weeks || 8,
      league.start_time,
      league.end_time,
      league.sport
    );
  }, [gameDates, league.date, league.total_weeks, league.start_time, league.end_time, timeRange]);

  // Interleave rest weeks
  const weeks = useMemo(() => {
    return interleaveRestWeeks(encounterWeeks, league.restWeeks || [], league.sport);
  }, [encounterWeeks, league.restWeeks, league.sport]);
  
  const weeksRemaining = calculateWeeksRemaining(weeks);

  // Function to calculate player totals
  const calculatePlayerTotals = (playerTeam: string) => {
    let totalGamesFor = 0;
    let totalGamesAgainst = 0;

    // Loop through all played weeks
    [1, 2, 3, 4].forEach(weekNumber => {
      const weekMatches = allMatchesByWeek[weekNumber] || [];
      weekMatches.forEach(match => {
        if (match.team1 === playerTeam) {
          const [gamesFor, gamesAgainst] = match.score.split('-').map(Number);
          totalGamesFor += gamesFor;
          totalGamesAgainst += gamesAgainst;
        } else if (match.team2 === playerTeam) {
          const [gamesAgainst, gamesFor] = match.score.split('-').map(Number);
          totalGamesFor += gamesFor;
          totalGamesAgainst += gamesAgainst;
        }
      });
    });

    return { totalGamesFor, totalGamesAgainst, difference: totalGamesFor - totalGamesAgainst };
  };

  // Calculate ranking based on games for
  const mockPlayers = league.status === 'upcoming' ? [
    { position: 1, name: "TBD", partner: "TBD", gamesFor: 0, gamesAgainst: 0, difference: 0, isCurrentUser: false },
    { position: 2, name: "TBD", partner: "TBD", gamesFor: 0, gamesAgainst: 0, difference: 0, isCurrentUser: false },
    { position: 3, name: "TBD", partner: "TBD", gamesFor: 0, gamesAgainst: 0, difference: 0, isCurrentUser: false },
    { position: 4, name: "TBD", partner: "TBD", gamesFor: 0, gamesAgainst: 0, difference: 0, isCurrentUser: false },
    { position: 5, name: "TBD", partner: "TBD", gamesFor: 0, gamesAgainst: 0, difference: 0, isCurrentUser: false },
    { position: 6, name: "TBD", partner: "TBD", gamesFor: 0, gamesAgainst: 0, difference: 0, isCurrentUser: false },
    { position: 7, name: "TBD", partner: "TBD", gamesFor: 0, gamesAgainst: 0, difference: 0, isCurrentUser: false },
    { position: 8, name: "TBD", partner: "TBD", gamesFor: 0, gamesAgainst: 0, difference: 0, isCurrentUser: false }
  ] : [
    { name: "TÚ", partner: "María González", team: "TÚ / María González" },
    { name: "Carlos Rivera", partner: "Laura Sánchez", team: "Carlos Rivera / Laura Sánchez" },
    { name: "Diego López", partner: "Carmen Ruiz", team: "Diego López / Carmen Ruiz" },
    { name: "Juan Pérez", partner: "Ana Martín", team: "Juan Pérez / Ana Martín" },
    { name: "Roberto Silva", partner: "Elena Torres", team: "Roberto Silva / Elena Torres" },
    { name: "Fernando Mora", partner: "Isabel Vega", team: "Fernando Mora / Isabel Vega" },
    { name: "Miguel Castro", partner: "Sofía Herrera", team: "Miguel Castro / Sofía Herrera" },
    { name: "Andrés Jiménez", partner: "Lucía Mendoza", team: "Andrés Jiménez / Lucía Mendoza" }
  ].map(player => {
    const totals = calculatePlayerTotals(player.team);
    return {
      ...player,
      gamesFor: totals.totalGamesFor,
      gamesAgainst: totals.totalGamesAgainst,
      difference: totals.difference,
      isCurrentUser: player.name === "TÚ"
    };
  }).sort((a, b) => {
    // Sort by games for, then by difference
    if (a.gamesFor !== b.gamesFor) return b.gamesFor - a.gamesFor;
    return b.difference - a.difference;
  }).map((player, index) => ({ ...player, position: index + 1 }));

  const handleWeekClick = (week: any) => {
    if (week.isRest) return;
    setSelectedCalendarWeek(week);
  };

  const handleAttendanceChange = (weekNumber: number, attendance: 'attending' | 'not-attending') => {
    // Save to database via hook
    updateAttendance(weekNumber, attendance);
  };

  // Handle encounter selection change
  const handleEncuentroChange = (weekNumber: number) => {
    setSelectedEncuentro(weekNumber);
    setMatchSelectedEncounter(weekNumber);
  };

  // Set initial encounter to current week
  useEffect(() => {
    if (!selectedEncuentro) {
      const currentWeek = weeks.find(w => w.status === 'current');
      if (currentWeek) {
        setSelectedEncuentro(currentWeek.number);
        setMatchSelectedEncounter(currentWeek.number);
      }
    }
  }, [weeks, selectedEncuentro, setMatchSelectedEncounter]);

  // Determine current/selected week for match data
  const currentWeekForMatch = selectedEncuentro 
    ? weeks.find(w => w.number === selectedEncuentro) 
    : (selectedCalendarWeek || weeks.find(w => w.status === 'current') || weeks[0]);

  // Get section title based on selected week
  const getSectionTitle = (week: any) => {
    if (!week) return "Partidos de la Semana Actual";
    
    if (week.status === 'past') {
      return `Resultados Semana ${week.number}`;
    } else if (week.status === 'future') {
      return `Partidos Programados Semana ${week.number}`;
    }
    return "Partidos de la Semana Actual";
  };

  // Get current matches based on selected week
  // Helper to get user attendance status for current encounter
  const getUserAttendanceForEncounter = (): 'confirmed' | 'not_attending' | 'pending' => {
    const encounterNumber = selectedEncuentro || 1;
    const status = uiAttendance[encounterNumber];
    if (status === 'attending') return 'confirmed';
    if (status === 'not-attending') return 'not_attending';
    return 'pending';
  };

  const getCurrentMatches = () => {
    const currentWeek = currentWeekForMatch;
    const weekNumber = currentWeek?.number || 1;

    // PRIORITY 1: If we have real match data from the database, use it (even for upcoming leagues)
    if (userMatchForEncounter && userTeam && opponentTeam) {
      const isPlayed = userMatchForEncounter.status === 'completed';
      
      const matchDate = userMatchForEncounter.scheduled_date 
        ? parseLocalDate(userMatchForEncounter.scheduled_date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })
        : currentWeek?.date || "";
      
      // Format time from scheduled_time  
      const matchTime = userMatchForEncounter.scheduled_time 
        ? userMatchForEncounter.scheduled_time.slice(0, 5)
        : currentWeek?.time || timeRange;
      
      return [
        {
          matchNumber: 1,
          playerName: userTeam.player1,
          partnerName: userTeam.player2 || "",
          opponent1: opponentTeam.player1,
          opponent2: opponentTeam.player2,
          time: matchTime,
          court: `${userMatchForEncounter.court_number || "1"}`,
          isPlayed,
          date: matchDate,
          isUpcoming: false,
          opponentAttendanceStatus,
          userAttendanceStatus: getUserAttendanceForEncounter()
        }
      ];
    }

    // PRIORITY 2: For upcoming leagues with no match data in DB, show TBD content
    if (league.status === 'upcoming') {
      return [
        {
          matchNumber: 1,
          playerName: "TÚ",
          partnerName: league.partner || "Por confirmar",
          opponent1: "Por confirmar",
          opponent2: "Por confirmar",
          time: "Por confirmar",
          court: "TBD",
          isPlayed: false,
          date: "Por confirmar",
          isUpcoming: true
        },
        {
          matchNumber: 2,
          playerName: "TÚ",
          partnerName: league.partner || "Por confirmar",
          opponent1: "Por confirmar",
          opponent2: "Por confirmar",
          time: "Por confirmar",
          court: "TBD",
          isPlayed: false,
          date: "Por confirmar",
          isUpcoming: true
        },
        {
          matchNumber: 3,
          playerName: "TÚ",
          partnerName: league.partner || "Por confirmar",
          opponent1: "Por confirmar",
          opponent2: "Por confirmar",
          time: "Por confirmar",
          court: "TBD",
          isPlayed: false,
          date: "Por confirmar",
          isUpcoming: true
        }
      ];
    }
    
    if (currentWeek && currentWeek.status === 'past' && allMatchesByWeek[weekNumber]) {
      return allMatchesByWeek[weekNumber].map(match => ({
        ...match,
        playerName: "TÚ",
        partnerName: "María González",
        opponent1: match.team2.split(' / ')[0],
        opponent2: match.team2.split(' / ')[1],
        isPlayed: true,
        date: `Sábado ${currentWeek.date}`
      }));
    } else if (currentWeek && currentWeek.status === 'future' && futureMatches[weekNumber]) {
      return futureMatches[weekNumber].map(match => ({
        ...match,
        playerName: "TÚ",
        partnerName: "María González",
        opponent1: match.opponent.split(' / ')[0],
        opponent2: match.opponent.split(' / ')[1],
        isPlayed: false,
        date: `Sábado ${currentWeek.date}`
      }));
    } else {
      // Current week - CONSISTENT with mock data
      return [
        {
          matchNumber: 1,
          playerName: "TÚ",
          partnerName: "María González", 
          opponent1: "Carlos Rivera",
          opponent2: "Laura Sánchez",
          time: "10:00",
          court: "Central",
          isPlayed: false,
          date: "Sábado 12 Abr"
        },
        {
          matchNumber: 2,
          playerName: "TÚ",
          partnerName: "María González",
          opponent1: "Diego López", 
          opponent2: "Carmen Ruiz",
          time: "10:20",
          court: "Central", 
          isPlayed: false,
          date: "Sábado 12 Abr"
        },
        {
          matchNumber: 3,
          playerName: "TÚ",
          partnerName: "María González",
          opponent1: "Roberto Silva",
          opponent2: "Elena Torres",
          time: "10:40",
          court: "Central",
          isPlayed: false,
          date: "Sábado 12 Abr"
        }
      ];
    }
  };

  // Calculate week summary
  const getWeekSummary = () => {
    const currentWeek = currentWeekForMatch;
    const weekNumber = currentWeek?.number || 1;
    
    if (currentWeek && currentWeek.status === 'past' && allMatchesByWeek[weekNumber]) {
      const weekMatches = allMatchesByWeek[weekNumber];
      let gamesFor = 0;
      let gamesAgainst = 0;
      
      weekMatches.forEach(match => {
        const [for1, against1] = match.score.split('-').map(Number);
        gamesFor += for1;
        gamesAgainst += against1;
      });
      
      return {
        gamesFor,
        gamesAgainst,
        difference: gamesFor - gamesAgainst
      };
    }
    
    return { gamesFor: 0, gamesAgainst: 0, difference: 0 };
  };

  const currentMatches = getCurrentMatches();
  const weekSummary = getWeekSummary();

  return (
    <div className="space-y-8">
      {/* League Information */}
      <LeagueInfo 
        leagueName={leagueData.leagueName}
        startDate={leagueData.startDate}
        endDate={leagueData.endDate}
        timeRange={formatTimeRange(league.start_time, league.end_time)}
        totalMatches={leagueData.totalMatches}
        matchesPlayed={leagueData.matchesPlayed}
        sport={leagueData.sport}
        modalidad={leagueData.modalidad}
        partner={leagueData.partner}
        club={leagueData.club}
        category={leagueData.category}
        subcategory={leagueData.subcategory}
        leagueType={leagueData.leagueType}
        allCategoryPrizes={allCategoryPrizes}
        userCategory={userCategory}
        gameRules={[
          { label: "Formato de Juego", value: "Partidos de 20 minutos" },
          { label: "Partidos por Día", value: "3 partidos por día de liga" },
          { label: "Objetivo", value: "Sumar la mayor cantidad de juegos" },
          { label: "Desempate", value: "Mayor diferencia J.Favor - J.Contra" }
        ]}
        onContactOrganizer={onContactOrganizer}
        groupRules={league.group_rules}
        additionalRules={league.additional_rules}
        gameDays={league.game_days}
        startTime={league.start_time}
        endTime={league.end_time}
        organizerName={league.organizer}
      />

      {/* Weekly Calendar */}
      <WeeklyCalendar 
        weeks={weeks} 
        weeksRemaining={league.status === 'upcoming' ? (league.total_weeks || 8) : weeksRemaining} 
        onWeekClick={handleWeekClick}
        onAttendanceChange={handleAttendanceChange}
        initialAttendance={uiAttendance}
        isUpdatingAttendance={isUpdatingAttendance}
        sport={league.sport}
      />

      {/* Registration Button - Solo si NO está inscrito */}
      {!isUserRegistered && onRegisterClick && (
        <div className="px-0">
          <Button
            onClick={REGISTRATION_ENABLED ? onRegisterClick : undefined}
            disabled={registrationLoading || !REGISTRATION_ENABLED}
            className={`w-full h-16 text-lg font-bold shadow-lg hover:shadow-xl transition-all ${
              !REGISTRATION_ENABLED
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
            }`}
          >
            {registrationLoading ? 'Cargando...' : '¡Inscríbeme!'}
          </Button>
        </div>
      )}

      {/* Matches Section */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          {getSectionTitle(currentWeekForMatch)}
        </h3>
        
        {/* Horizontal scrollable matches */}
        <div className="overflow-x-auto">
          <div className="flex gap-4 pb-2" style={{ minWidth: 'max-content' }}>
            {currentMatches.map((match, index) => (
              <div key={index} className={`
                flex-shrink-0 w-80 rounded-lg border-2 p-4 transition-all duration-200 hover:shadow-md
                ${match.isPlayed 
                  ? 'bg-gray-50 border-gray-300' 
                  : match.isUpcoming 
                    ? 'bg-gray-50 border-gray-200' 
                    : 'bg-gradient-to-r from-primary/5 to-primary/10 border-primary/30'
                }
              `}>
                {/* Match header */}
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-2">
                    <span className="bg-white text-gray-700 px-2 py-1 rounded-md text-sm font-medium border">
                      Partido {match.matchNumber}/3
                    </span>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <div className="font-medium">{match.time}</div>
                    <div className="text-xs">Cancha {match.court}</div>
                  </div>
                </div>

                {/* Teams with Scores */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="font-medium text-gray-900 text-sm">
                        {match.playerName} / {match.partnerName}
                      </span>
                    </div>
                    {match.isPlayed && match.score && (
                      <span className="bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
                        {getTeamScore(match.score, true)}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <span className="text-gray-400 font-bold">VS</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="font-medium text-gray-900 text-sm">
                        {match.opponent1} / {match.opponent2}
                      </span>
                    </div>
                    {match.isPlayed && match.score && (
                      <span className="bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
                        {getTeamScore(match.score, false)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Status for unplayed matches */}
                {!match.isPlayed && (
                  <div className="flex justify-center">
                    {!match.isUpcoming ? (
                      <span className="bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-medium">
                        Por jugar
                      </span>
                    ) : (
                      <span className="bg-gray-400 text-white px-4 py-2 rounded-full text-sm font-medium">
                        TBD
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Scroll indicator for mobile */}
        <div className="mt-2 flex justify-center md:hidden">
          <div className="text-xs text-gray-400 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
            </svg>
            Desliza para ver más partidos
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>
        </div>
        
        {/* Week Summary */}
        {weekSummary.gamesFor > 0 && (
          <div className="mt-6 bg-gradient-to-r from-primary to-primary/60 rounded-lg p-4 text-primary-foreground text-center">
            <div className="font-semibold mb-1">Resumen de la Semana</div>
            <div className="text-sm">
              Juegos Sumados: {weekSummary.gamesFor} | 
              Juegos en Contra: {weekSummary.gamesAgainst} | 
              Diferencia: {weekSummary.difference >= 0 ? '+' : ''}{weekSummary.difference}
            </div>
          </div>
        )}
      </div>

      {/* Ranking Table */}
      <LeagueRankingTable 
        players={mockPlayers} 
        modalidad={leagueData.modalidad} 
        type="sumatoria-juegos"
      />
    </div>
  );
};

export default LeagueSumatoriaJuegosView;